import React, { useState, useEffect, useLayoutEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import LinkButton from './LinkButton';
import { makeMatcher } from '../searchMatch';

// The hamburger menu: a short root list whose groups branch out, plus a search
// box that looks through every leaf at once, ignoring the levels.
//
// Items are a tree:
//   { key, label, icon?, sub?, href?, onSelect?, disabled?, title?, keywords?,
//     accent?, muted?, items? }           — `items` makes it a group
//   { sep: true }                          — a thin separator line
//
// Wide screens: a group opens a flyout panel beside the root (hover, click or
// the right-arrow key). Narrow screens: the group replaces the root panel in
// place, with a "Back" row on top. Typing in the search box flattens everything
// into one list of matches labelled "Group › Item"; arrows move, Enter runs,
// Esc clears.

const NARROW_QUERY = '(max-width: 768px)';
const ROOT_WIDTH = 250;
const FLYOUT_WIDTH = 230;
const PAD = 10;

const useNarrow = () => {
  const [narrow, setNarrow] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(NARROW_QUERY).matches
  );
  useEffect(() => {
    const mq = window.matchMedia(NARROW_QUERY);
    const on = (e) => setNarrow(e.matches);
    if (mq.addEventListener) mq.addEventListener('change', on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener('change', on); else mq.removeListener(on); };
  }, []);
  return narrow;
};

const isGroup = (it) => Array.isArray(it.items);

// Every leaf with the path of group labels above it, in menu order.
const flattenLeaves = (items, crumb = []) => {
  const out = [];
  items.forEach((it) => {
    if (!it || it.sep) return;
    if (isGroup(it)) out.push(...flattenLeaves(it.items, [...crumb, it.label]));
    else out.push({ ...it, crumb });
  });
  return out;
};

// All words of the query have to appear somewhere in label / keywords / crumb;
// a query typed in the wrong keyboard layout counts too (see searchMatch).
const searchLeaves = (leaves, query) => {
  const match = makeMatcher(query);
  const scored = [];
  leaves.forEach((leaf, order) => {
    const score = match([leaf.label, ...(leaf.keywords || []), ...leaf.crumb]);
    if (score) scored.push({ leaf, score, order });
  });
  scored.sort((a, b) => b.score - a.score || a.order - b.order);
  return scored.map((s) => s.leaf);
};

const MainMenu = ({ open, onClose, anchorRef, items, focusToken = 0 }) => {
  const narrow = useNarrow();
  const rootRef = useRef(null);
  const flyoutRef = useRef(null);
  const inputRef = useRef(null);
  const groupRowRefs = useRef({});
  const closeTimer = useRef(null);

  const [query, setQuery] = useState('');
  const [openGroupKey, setOpenGroupKey] = useState(null); // flyout (wide) or drilled group (narrow)
  const [flyoutPos, setFlyoutPos] = useState(null);
  const [panel, setPanel] = useState('root'); // which list the arrows move in: 'root' | 'sub'
  const [active, setActive] = useState(-1);

  const visibleItems = useMemo(() => (items || []).filter(Boolean), [items]);
  const leaves = useMemo(() => flattenLeaves(visibleItems), [visibleItems]);
  const results = useMemo(() => searchLeaves(leaves, query), [leaves, query]);
  const searching = query.trim().length > 0;
  const openGroup = openGroupKey ? visibleItems.find((it) => isGroup(it) && it.key === openGroupKey) : null;

  // Start clean every time the menu opens.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setOpenGroupKey(null);
    setFlyoutPos(null);
    setPanel('root');
    setActive(-1);
  }, [open]);

  // Focus the search box on open and on every Ctrl+K while already open.
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 0);
    return () => clearTimeout(t);
  }, [open, focusToken]);

  // A click outside the button, the root panel and the flyout closes the menu.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      const inside = (ref) => ref && ref.current && ref.current.contains(e.target);
      if (inside(anchorRef) || inside(rootRef) || inside(flyoutRef)) return;
      onClose();
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open, onClose, anchorRef]);

  useEffect(() => () => clearTimeout(closeTimer.current), []);

  const cancelClose = () => clearTimeout(closeTimer.current);

  // Where the flyout goes: beside the root at the height of its group row,
  // flipped to the left when there is no room on the right.
  const placeFlyout = useCallback((key) => {
    const row = groupRowRefs.current[key];
    const root = rootRef.current;
    if (!row || !root) return;
    const rr = row.getBoundingClientRect();
    const pr = root.getBoundingClientRect();
    let left = pr.right - 1;
    if (left + FLYOUT_WIDTH > window.innerWidth - PAD) left = Math.max(PAD, pr.left - FLYOUT_WIDTH + 1);
    setFlyoutPos({ top: Math.round(rr.top - 5), left: Math.round(left) });
  }, []);

  // Once rendered, pull the flyout up if its bottom runs off the screen.
  useLayoutEffect(() => {
    if (narrow || !openGroupKey || !flyoutRef.current || !flyoutPos) return;
    const h = flyoutRef.current.offsetHeight;
    const maxTop = window.innerHeight - PAD - h;
    if (flyoutPos.top > maxTop) setFlyoutPos((p) => ({ ...p, top: Math.max(PAD, maxTop) }));
  }, [narrow, openGroupKey, flyoutPos]);

  const openGroupByKey = (key, viaKeyboard) => {
    cancelClose();
    setOpenGroupKey(key);
    if (narrow) {
      setPanel('sub');
      setActive(-1);
    } else {
      placeFlyout(key);
      if (viaKeyboard) { setPanel('sub'); setActive(0); }
    }
  };

  const closeSub = () => {
    setOpenGroupKey(null);
    setFlyoutPos(null);
    setPanel('root');
  };

  const activate = (item) => {
    if (!item || item.disabled) return;
    if (isGroup(item)) { openGroupByKey(item.key, true); return; }
    if (item.onSelect) item.onSelect();
    onClose();
  };

  // The list that the arrows and Enter act on right now.
  const rows = searching
    ? results
    : (panel === 'sub' && openGroup)
      ? openGroup.items.filter((it) => it && !it.sep)
      : visibleItems.filter((it) => !it.sep);

  const step = (dir) => {
    if (!rows.length) return;
    let i = active;
    for (let n = 0; n < rows.length; n++) {
      i = (i + dir + rows.length) % rows.length;
      if (!rows[i].disabled) break;
    }
    setActive(i);
  };

  useEffect(() => {
    const host = panel === 'sub' && !narrow ? flyoutRef.current : rootRef.current;
    const el = host && host.querySelector('.mm-row.active');
    if (el && el.scrollIntoView) el.scrollIntoView({ block: 'nearest' });
  }, [active, panel, narrow]);

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (searching) setQuery('');
      else if (openGroupKey) closeSub();
      else onClose();
      return;
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); step(1); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); step(-1); return; }
    if (e.key === 'Enter') {
      if (active < 0) {
        if (searching && results.length) activate(results.find((r) => !r.disabled));
        return;
      }
      e.preventDefault();
      activate(rows[active]);
      return;
    }
    if (searching) return;
    if (e.key === 'ArrowRight') {
      const it = rows[active];
      if (it && isGroup(it)) { e.preventDefault(); openGroupByKey(it.key, true); }
      return;
    }
    if (e.key === 'ArrowLeft') {
      if (openGroupKey) {
        e.preventDefault();
        if (narrow) closeSub();
        else { setPanel('root'); setActive(Math.max(0, visibleItems.filter((it) => !it.sep).findIndex((it) => it.key === openGroupKey))); }
      }
    }
  };

  const onQueryChange = (e) => {
    const v = e.target.value;
    setQuery(v);
    setActive(v.trim() ? 0 : -1);
    if (v.trim()) { setOpenGroupKey(null); setFlyoutPos(null); setPanel('root'); }
  };

  if (!open || !anchorRef || !anchorRef.current) return null;

  const a = anchorRef.current.getBoundingClientRect();
  const rootWidth = narrow ? Math.min(300, window.innerWidth - 2 * PAD) : ROOT_WIDTH;
  let left = a.left;
  if (left + rootWidth > window.innerWidth - PAD) left = window.innerWidth - rootWidth - PAD;
  if (left < PAD) left = PAD;
  const top = a.bottom + 5;
  const rootStyle = { top, left, width: rootWidth, maxHeight: `calc(100vh - ${Math.round(top) + PAD}px)` };

  const rowClass = (item, isActive) => [
    'dropdown-item', 'mm-row',
    isGroup(item) ? 'mm-row-group' : '',
    item.accent ? 'dropdown-item-new' : '',
    item.muted ? 'mm-row-muted' : '',
    isActive ? 'active' : '',
  ].filter(Boolean).join(' ');

  const renderLeaf = (item, idx, inPanel, crumb) => (
    <LinkButton
      key={item.key}
      className={rowClass(item, panel === inPanel && active === idx)}
      href={item.href}
      disabled={item.disabled}
      title={item.title}
      onClick={() => activate(item)}
      onMouseEnter={() => {
        if (item.disabled) return;
        setPanel(inPanel); setActive(idx);
        // Resting on a plain root item folds the flyout, with a short grace
        // period so a diagonal move towards the flyout does not lose it.
        if (inPanel === 'root' && !narrow && openGroupKey && !searching) {
          cancelClose();
          closeTimer.current = setTimeout(closeSub, 250);
        } else cancelClose();
      }}
    >
      <span className="mm-icon" aria-hidden="true">{item.icon || ''}</span>
      {crumb && crumb.length ? <span className="mm-crumb">{crumb.join(' › ')} ›</span> : null}
      <span className="mm-label">{item.label}</span>
    </LinkButton>
  );

  const renderGroupRow = (item, idx) => (
    <button
      key={item.key}
      type="button"
      ref={(el) => { groupRowRefs.current[item.key] = el; }}
      className={rowClass(item, panel === 'root' && active === idx) + (openGroupKey === item.key ? ' open' : '')}
      title={item.title}
      onClick={() => (openGroupKey === item.key && !narrow ? closeSub() : openGroupByKey(item.key, false))}
      onMouseEnter={() => {
        setPanel('root'); setActive(idx);
        if (!narrow) openGroupByKey(item.key, false);
      }}
    >
      <span className="mm-icon" aria-hidden="true">{item.icon || ''}</span>
      <span className="mm-label">{item.label}</span>
      {item.sub ? <span className="mm-sub">{item.sub}</span> : null}
      <span className="mm-chevron" aria-hidden="true">›</span>
    </button>
  );

  const renderList = (list, inPanel, withCrumbs) => {
    let idx = -1;
    return list.map((item, i) => {
      if (!item) return null;
      if (item.sep) return <div key={`sep-${i}`} className="mm-sep" />;
      idx += 1;
      if (isGroup(item)) return renderGroupRow(item, idx);
      return renderLeaf(item, idx, inPanel, withCrumbs ? item.crumb : null);
    });
  };

  let body;
  if (searching) {
    body = results.length
      ? renderList(results, 'root', true)
      : <div className="mm-empty">Nothing matches “{query.trim()}”</div>;
  } else if (narrow && openGroup) {
    body = (
      <>
        <button type="button" className="dropdown-item mm-row mm-back" onClick={closeSub}>
          <span className="mm-chevron mm-chevron-back" aria-hidden="true">‹</span>
          <span className="mm-label">{openGroup.label}</span>
          {openGroup.sub ? <span className="mm-sub">{openGroup.sub}</span> : null}
        </button>
        {renderList(openGroup.items, 'sub', false)}
      </>
    );
  } else {
    body = renderList(visibleItems, 'root', false);
  }

  return ReactDOM.createPortal(
    <>
      <div className="dropdown-menu mm-root" style={rootStyle} ref={rootRef} onKeyDown={onKeyDown} role="menu">
        <div className="mm-search">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={onQueryChange}
            placeholder="Search menu…  Ctrl+K"
            aria-label="Search menu"
            autoComplete="off"
            spellCheck={false}
          />
          {query ? (
            <button
              type="button"
              className="mm-search-clear"
              onClick={() => { setQuery(''); setActive(-1); inputRef.current && inputRef.current.focus(); }}
              aria-label="Clear search"
            >
              ×
            </button>
          ) : null}
        </div>
        <div className="mm-list">{body}</div>
      </div>
      {!narrow && !searching && openGroup && flyoutPos && (
        <div
          className="dropdown-menu mm-flyout"
          style={{ top: flyoutPos.top, left: flyoutPos.left, width: FLYOUT_WIDTH }}
          ref={flyoutRef}
          onMouseEnter={cancelClose}
        >
          <div className="mm-flyout-head">
            {openGroup.label}
            {openGroup.sub ? <span className="mm-sub">{openGroup.sub}</span> : null}
          </div>
          {renderList(openGroup.items, 'sub', false)}
        </div>
      )}
    </>,
    document.body
  );
};

export default MainMenu;
